import { useState, useEffect, useCallback } from 'react';
import { generateClient, GraphQLResult } from 'aws-amplify/api';
import { useAuth } from '../../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import {
  Users,
  UsersRound,
  Plus,
  Trash2,
  UserPlus,
  Shield,
  User,
  X,
  Loader2,
} from 'lucide-react';
import { LIST_USERS, LIST_TEAMS } from '../../graphql/queries';
import {
  UPDATE_USER_ROLE,
  CREATE_TEAM,
  DELETE_TEAM,
  ADD_TEAM_MEMBER,
  REMOVE_TEAM_MEMBER,
} from '../../graphql/mutations';

const client = generateClient();

type UserRole = 'ADMIN' | 'USER';
type TeamRole = 'ADMIN' | 'MEMBER';

interface UserType {
  userId: string;
  email: string;
  role: UserRole;
  defaultTeamId?: string;
  createdAt: string;
  updatedAt: string;
}

interface TeamMember {
  teamId: string;
  userId: string;
  role: TeamRole;
  email?: string;
  joinedAt: string;
}

interface Team {
  teamId: string;
  name: string;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
  members?: TeamMember[];
}

interface ListUsersResponse {
  listUsers: { items: UserType[]; nextToken?: string };
}

interface ListTeamsResponse {
  listTeams: { items: Team[]; nextToken?: string };
}

export function AdminPage() {
  const { isAdmin, isLoading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<'users' | 'teams'>('users');
  const [users, setUsers] = useState<UserType[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [isCreateTeamModalOpen, setIsCreateTeamModalOpen] = useState(false);
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [newTeamName, setNewTeamName] = useState('');
  const [selectedMemberUserId, setSelectedMemberUserId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await client.graphql({
        query: LIST_USERS,
        variables: { limit: 100 },
      }) as GraphQLResult<ListUsersResponse>;
      setUsers(response.data?.listUsers?.items || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    }
  }, []);

  const fetchTeams = useCallback(async () => {
    try {
      const response = await client.graphql({
        query: LIST_TEAMS,
        variables: { limit: 100 },
      }) as GraphQLResult<ListTeamsResponse>;
      setTeams(response.data?.listTeams?.items || []);
    } catch (err) {
      console.error('Error fetching teams:', err);
      setError('Failed to load teams');
    }
  }, []);

  useEffect(() => {
    async function loadData() {
      setIsLoading(true);
      setError(null);
      await Promise.all([fetchUsers(), fetchTeams()]);
      setIsLoading(false);
    }
    if (isAdmin) {
      loadData();
    }
  }, [isAdmin, fetchUsers, fetchTeams]);

  // Redirect non-admins
  if (authLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin-slow w-8 h-8 border-2 border-coral border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  async function handleToggleRole(userId: string, currentRole: UserRole) {
    const newRole = currentRole === 'ADMIN' ? 'USER' : 'ADMIN';
    try {
      await client.graphql({
        query: UPDATE_USER_ROLE,
        variables: { userId, role: newRole },
      });
      setUsers(users.map(u => (u.userId === userId ? { ...u, role: newRole } : u)));
    } catch (err) {
      console.error('Error updating user role:', err);
      alert('Failed to update user role');
    }
  }

  async function handleCreateTeam() {
    if (!newTeamName.trim()) return;
    setIsSubmitting(true);
    try {
      await client.graphql({
        query: CREATE_TEAM,
        variables: { input: { name: newTeamName.trim() } },
      });
      await fetchTeams();
      setNewTeamName('');
      setIsCreateTeamModalOpen(false);
    } catch (err) {
      console.error('Error creating team:', err);
      alert('Failed to create team');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDeleteTeam(teamId: string) {
    if (!confirm('Are you sure you want to delete this team? This cannot be undone.')) return;
    try {
      await client.graphql({
        query: DELETE_TEAM,
        variables: { teamId },
      });
      setTeams(teams.filter(t => t.teamId !== teamId));
    } catch (err) {
      console.error('Error deleting team:', err);
      alert('Failed to delete team');
    }
  }

  async function handleAddMember() {
    if (!selectedTeamId || !selectedMemberUserId) return;
    const selectedUser = users.find(u => u.userId === selectedMemberUserId);
    if (!selectedUser) return;

    setIsSubmitting(true);
    try {
      const response = await client.graphql({
        query: ADD_TEAM_MEMBER,
        variables: {
          input: {
            teamId: selectedTeamId,
            email: selectedUser.email.toLowerCase(),
            role: 'MEMBER',
          },
        },
      }) as GraphQLResult<{ addTeamMember: TeamMember }>;

      const newMember = response.data?.addTeamMember;
      if (newMember) {
        // Update local state optimistically
        setTeams(teams.map(team =>
          team.teamId === selectedTeamId
            ? { ...team, members: [...(team.members || []), newMember] }
            : team
        ));
      }
      setSelectedMemberUserId('');
      setIsAddMemberModalOpen(false);
      setSelectedTeamId(null);
    } catch (err: unknown) {
      console.error('Error adding team member:', err);
      const error = err as { errors?: { message: string }[] };
      alert(error?.errors?.[0]?.message || 'Failed to add team member');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleRemoveMember(teamId: string, userId: string) {
    if (!confirm('Remove this member from the team?')) return;
    try {
      await client.graphql({
        query: REMOVE_TEAM_MEMBER,
        variables: { teamId, userId },
      });
      // Update local state optimistically
      setTeams(teams.map(team =>
        team.teamId === teamId
          ? { ...team, members: team.members?.filter(m => m.userId !== userId) }
          : team
      ));
    } catch (err: unknown) {
      console.error('Error removing team member:', err);
      const error = err as { errors?: { message: string }[] };
      alert(error?.errors?.[0]?.message || 'Failed to remove team member');
    }
  }

  return (
    <div className="py-8">
      <div className="container-content">
        {/* Header */}
        <div className="section-dark rounded-card p-8 mb-8">
          <h1 className="text-heading-1 font-bold text-white mb-2">Admin Settings</h1>
          <p className="text-body text-white/70">Manage users, roles, and teams</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('users')}
            className={`flex items-center gap-2 px-4 py-2 rounded-card transition-colors ${
              activeTab === 'users'
                ? 'bg-coral text-white'
                : 'bg-white text-gray-dark hover:bg-gray-light'
            }`}
          >
            <Users size={18} />
            Users
          </button>
          <button
            onClick={() => setActiveTab('teams')}
            className={`flex items-center gap-2 px-4 py-2 rounded-card transition-colors ${
              activeTab === 'teams'
                ? 'bg-coral text-white'
                : 'bg-white text-gray-dark hover:bg-gray-light'
            }`}
          >
            <UsersRound size={18} />
            Teams
          </button>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin-slow w-8 h-8 border-2 border-coral border-t-transparent rounded-full" />
          </div>
        ) : error ? (
          <div className="card text-center py-12">
            <p className="text-status-error mb-4">{error}</p>
            <button onClick={() => { fetchUsers(); fetchTeams(); }} className="btn-secondary">
              Try Again
            </button>
          </div>
        ) : activeTab === 'users' ? (
          /* Users Tab */
          <div className="card">
            <div className="p-4 border-b border-gray-light">
              <h2 className="text-heading-3 font-semibold">All Users ({users.length})</h2>
            </div>
            <div className="divide-y divide-gray-light">
              {users.map(user => (
                <div key={user.userId} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        user.role === 'ADMIN' ? 'bg-coral/10' : 'bg-gray-light'
                      }`}
                    >
                      {user.role === 'ADMIN' ? (
                        <Shield size={20} className="text-coral" />
                      ) : (
                        <User size={20} className="text-gray-medium" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-dark">{user.email}</p>
                      <p className="text-body-sm text-gray-medium">
                        {user.role === 'ADMIN' ? 'Administrator' : 'User'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleToggleRole(user.userId, user.role)}
                    className={`px-4 py-2 rounded-card text-body-sm font-medium transition-colors ${
                      user.role === 'ADMIN'
                        ? 'bg-gray-light text-gray-dark hover:bg-gray-200'
                        : 'bg-coral/10 text-coral hover:bg-coral/20'
                    }`}
                  >
                    {user.role === 'ADMIN' ? 'Remove Admin' : 'Make Admin'}
                  </button>
                </div>
              ))}
              {users.length === 0 && (
                <div className="p-8 text-center text-gray-medium">No users found</div>
              )}
            </div>
          </div>
        ) : (
          /* Teams Tab */
          <div>
            <div className="flex justify-end mb-4">
              <button
                onClick={() => setIsCreateTeamModalOpen(true)}
                className="btn-primary flex items-center gap-2"
              >
                <Plus size={20} />
                Create Team
              </button>
            </div>

            {teams.length === 0 ? (
              <div className="card text-center py-12">
                <UsersRound size={48} className="mx-auto text-gray-medium mb-4" />
                <h2 className="text-heading-3 font-semibold mb-2">No teams yet</h2>
                <p className="text-body text-gray-medium mb-6">
                  Create a team to share site access between users
                </p>
                <button
                  onClick={() => setIsCreateTeamModalOpen(true)}
                  className="btn-primary"
                >
                  <Plus size={20} className="mr-2" />
                  Create Your First Team
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {teams.map(team => (
                  <div key={team.teamId} className="card">
                    <div className="p-4 border-b border-gray-light flex items-center justify-between">
                      <h3 className="text-heading-3 font-semibold">{team.name}</h3>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setSelectedTeamId(team.teamId);
                            setIsAddMemberModalOpen(true);
                          }}
                          className="btn-secondary flex items-center gap-2 text-body-sm"
                        >
                          <UserPlus size={16} />
                          Add Member
                        </button>
                        <button
                          onClick={() => handleDeleteTeam(team.teamId)}
                          className="p-2 text-status-error hover:bg-red-50 rounded-card transition-colors"
                          title="Delete team"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                    <div className="divide-y divide-gray-light">
                      {team.members?.map(member => (
                        <div
                          key={member.userId}
                          className="p-4 flex items-center justify-between"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gray-light flex items-center justify-center">
                              <User size={16} className="text-gray-medium" />
                            </div>
                            <div>
                              <p className="text-body font-medium">{member.email || member.userId}</p>
                              <p className="text-body-sm text-gray-medium">
                                {member.role === 'ADMIN' ? 'Team Admin' : 'Member'}
                              </p>
                            </div>
                          </div>
                          {team.ownerId !== member.userId && (
                            <button
                              onClick={() => handleRemoveMember(team.teamId, member.userId)}
                              className="text-gray-medium hover:text-status-error transition-colors"
                              title="Remove member"
                            >
                              <X size={18} />
                            </button>
                          )}
                        </div>
                      ))}
                      {(!team.members || team.members.length === 0) && (
                        <div className="p-4 text-center text-gray-medium text-body-sm">
                          No members yet
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Team Modal */}
      {isCreateTeamModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-card p-6 w-full max-w-md mx-4">
            <h2 className="text-heading-3 font-semibold mb-4">Create Team</h2>
            <input
              type="text"
              value={newTeamName}
              onChange={e => setNewTeamName(e.target.value)}
              placeholder="Team name"
              className="input w-full mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsCreateTeamModalOpen(false);
                  setNewTeamName('');
                }}
                className="btn-secondary"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTeam}
                className="btn-primary flex items-center gap-2"
                disabled={!newTeamName.trim() || isSubmitting}
              >
                {isSubmitting && <Loader2 size={16} className="animate-spin" />}
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {isAddMemberModalOpen && selectedTeamId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-card p-6 w-full max-w-md mx-4">
            <h2 className="text-heading-3 font-semibold mb-4">Add Team Member</h2>
            <p className="text-body-sm text-gray-medium mb-4">
              Select a user to add to this team.
            </p>
            {(() => {
              const selectedTeam = teams.find(t => t.teamId === selectedTeamId);
              const existingMemberIds = new Set(selectedTeam?.members?.map(m => m.userId) || []);
              const availableUsers = users.filter(u => !existingMemberIds.has(u.userId));

              return availableUsers.length === 0 ? (
                <div className="text-center py-4 text-gray-medium">
                  <p>All users are already members of this team.</p>
                </div>
              ) : (
                <select
                  value={selectedMemberUserId}
                  onChange={e => setSelectedMemberUserId(e.target.value)}
                  className="input w-full mb-4"
                  autoFocus
                >
                  <option value="">Select a user...</option>
                  {availableUsers.map(user => (
                    <option key={user.userId} value={user.userId}>
                      {user.email} {user.role === 'ADMIN' ? '(Admin)' : ''}
                    </option>
                  ))}
                </select>
              );
            })()}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsAddMemberModalOpen(false);
                  setSelectedMemberUserId('');
                  setSelectedTeamId(null);
                }}
                className="btn-secondary"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleAddMember}
                className="btn-primary flex items-center gap-2"
                disabled={!selectedMemberUserId || isSubmitting}
              >
                {isSubmitting && <Loader2 size={16} className="animate-spin" />}
                Add Member
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
